"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { toast } from "sonner";
import { useAuthStore } from "@/store/useAuthStore";
import { useIncomingCallStore } from "@/store/useIncomingCallStore";
import { getSocket } from "@/hooks/useSocket";
import { getCookie } from "@/utils/cookie";

const CALL_EVENTS = {
  INVITE: "call:invite",
  REJECT: "call:reject",
  CANCEL: "call:cancel",
  END: "call:end",
};

const isSameUserId = (left, right) =>
  left != null && right != null && String(left) === String(right);

const getCallerName = (payload) =>
  payload?.caller?.name ||
  [payload?.caller?.firstName, payload?.caller?.lastName]
    .filter(Boolean)
    .join(" ") ||
  "Someone";

const SocketCallManager = () => {
  const router = useRouter();
  const pathname = usePathname();
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const user = useAuthStore((state) => state.user);
  const incomingCall = useIncomingCallStore((state) => state.incomingCall);
  const setIncomingCall = useIncomingCallStore((state) => state.setIncomingCall);
  const clearIncomingCall = useIncomingCallStore(
    (state) => state.clearIncomingCall,
  );

  useEffect(() => {
    if (!isLoggedIn && !getCookie("access_token")) {
      clearIncomingCall();
      return undefined;
    }

    const socket = getSocket();
    if (!socket || !socket.connected) return undefined;

    const clearIfMatchingCall = (payload) => {
      if (!payload?.callId) return;
      if (incomingCall?.callId === payload.callId) {
        clearIncomingCall();
      }
    };

    const handleInvite = (payload) => {
      if (incomingCall?.callId && incomingCall.callId === payload?.callId) {
        return;
      }

      if (user?.id && payload?.recipientId && !isSameUserId(payload.recipientId, user.id)) {
        return;
      }

      setIncomingCall(payload);
      toast.info(
        payload?.callType === "video"
          ? `Incoming video call from ${getCallerName(payload)}`
          : `Incoming voice call from ${getCallerName(payload)}`,
      );

      if (pathname !== "/messages") {
        router.push("/messages");
      }
    };

    socket.on(CALL_EVENTS.INVITE, handleInvite);
    socket.on(CALL_EVENTS.REJECT, clearIfMatchingCall);
    socket.on(CALL_EVENTS.CANCEL, clearIfMatchingCall);
    socket.on(CALL_EVENTS.END, clearIfMatchingCall);

    return () => {
      socket.off(CALL_EVENTS.INVITE, handleInvite);
      socket.off(CALL_EVENTS.REJECT, clearIfMatchingCall);
      socket.off(CALL_EVENTS.CANCEL, clearIfMatchingCall);
      socket.off(CALL_EVENTS.END, clearIfMatchingCall);
    };
  }, [
    clearIncomingCall,
    incomingCall?.callId,
    isLoggedIn,
    pathname,
    router,
    setIncomingCall,
    user?.id,
  ]);

  return null;
};

export default SocketCallManager;
